import { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import axios from "../../api/axios";
import { useCRUD } from "../../api/crud";
import { ERROR_SNACKBAR, SUCCESS_SNACKBAR } from "../../consts";
import CustomCircularProgress from "../ui/customCircularProgress/customCircularProgress.component";
import AddressForm from "../ui/addressForm/addressForm.component";
import styles from "./clientForm.module.css";

export interface OpeningHourRow {
  id?: string;
  week_day: string;
  from_hour: string;
  to_hour: string;
}

export interface ClientFormData {
  name: string;
  short_name: string;
  pesel: string;
  nip: string;
  regon: string;
  krs: string;
  country: string;
  city: string;
  street: string;
  zip_code: string;
  phone_number: string;
  email: string;
  email_invoices: string;
  notes: string;
  opening_hours: OpeningHourRow[];
}

const defaultFormData: ClientFormData = {
  name: "",
  short_name: "",
  pesel: "",
  nip: "",
  regon: "",
  krs: "",
  country: "PL",
  city: "",
  street: "",
  zip_code: "",
  phone_number: "",
  email: "",
  email_invoices: "",
  notes: "",
  opening_hours: [],
};

const normalizeNip = (value: string) => value.replace(/\D/g, "").slice(0, 10);

function getError(field: string, error: any): string | null {
  const fieldErrors = error?.[field];
  return Array.isArray(fieldErrors) && fieldErrors.length > 0
    ? fieldErrors[0]?.message ?? null
    : null;
}

export interface ClientFormProps {
  url: string;
  id?: string;
  onSuccess?: () => void;
}

const VERIFY_NIP_PATH = "verify-nip/";

export default function ClientForm({ url, id, onSuccess }: ClientFormProps) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { get, create, updatePUT, loading, error } = useCRUD(url);
  const [formData, setFormData] = useState<ClientFormData>(defaultFormData);
  const [isLoadingInitial, setIsLoadingInitial] = useState(!!id);
  const [nipToVerify, setNipToVerify] = useState("");
  const [verifyLoading, setVerifyLoading] = useState(false);

  const verifyNip = async () => {
    const nip = normalizeNip(nipToVerify.trim());
    if (!nip) {
      enqueueSnackbar(t("Please enter NIP"), ERROR_SNACKBAR);
      return;
    }
    setVerifyLoading(true);
    try {
      const base = url.endsWith("/") ? url.slice(0, -1) : url;
      const { data } = await axios.get<{ name?: string; nip?: string; regon?: string; krs?: string; country?: string; city?: string; street?: string; zip_code?: string }>(`${base}/${VERIFY_NIP_PATH}`, { params: { nip } });
      const fetchedNip = data.nip ?? "";
      setNipToVerify(fetchedNip);
      setFormData((prev) => ({
        ...prev,
        name: data.name ?? prev.name,
        nip: fetchedNip,
        regon: data.regon ?? prev.regon,
        krs: data.krs != null ? String(data.krs) : prev.krs,
        country: data.country ?? prev.country,
        city: data.city ?? prev.city,
        street: data.street ?? prev.street,
        zip_code: data.zip_code ?? prev.zip_code,
      }));
      enqueueSnackbar(t("Company data loaded."), SUCCESS_SNACKBAR);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        enqueueSnackbar(t("Company not found for this NIP."), ERROR_SNACKBAR);
      } else {
        const msg = err?.response?.data?.error ?? t("Failed to verify NIP.");
        enqueueSnackbar(typeof msg === "string" ? msg : t("Failed to verify NIP."), ERROR_SNACKBAR);
      }
    } finally {
      setVerifyLoading(false);
    }
  };

  useEffect(() => {
    if (!id) {
      setFormData({ ...defaultFormData });
      setNipToVerify("");
      return;
    }
    get(id).then((response) => {
      if (!response?.data) {
        setIsLoadingInitial(false);
        return;
      }
      const d = response.data;
      const address = d.address || {};
      const toTime = (v: string) => (v ? String(v).slice(0, 5) : "");
      const openingHours: OpeningHourRow[] = Array.isArray(address.opening_hours)
        ? address.opening_hours.map((oh: any) => ({
            ...(oh.id != null && oh.id !== "" ? { id: String(oh.id) } : {}),
            week_day: oh.week_day ?? "",
            from_hour: toTime(oh.from_hour ?? ""),
            to_hour: toTime(oh.to_hour ?? ""),
          }))
        : [];
      setFormData({
        name: d.name ?? "",
        short_name: d.short_name ?? "",
        pesel: d.pesel ?? "",
        nip: d.nip ?? "",
        regon: d.regon ?? "",
        krs: d.krs ?? "",
        country: address.country ?? "PL",
        city: address.city ?? "",
        street: address.street ?? "",
        zip_code: address.zip_code ?? "",
        phone_number: address.phone_number ?? "",
        email: address.email ?? "",
        email_invoices: address.email_invoices ?? "",
        notes: address.notes ?? "",
        opening_hours: openingHours,
      });
      setNipToVerify(d.nip ?? "");
      setIsLoadingInitial(false);
    }).catch(() => setIsLoadingInitial(false));
  }, [id, get]);

  const updateField = (field: keyof ClientFormData, value: string | OpeningHourRow[]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const buildPayload = (): Record<string, unknown> => {
    const payload: Record<string, unknown> = {
      name: formData.name || undefined,
      short_name: formData.short_name || undefined,
      pesel: formData.pesel || undefined,
      nip: formData.nip ?? "",
      regon: formData.regon || undefined,
      krs: formData.krs || undefined,
      country: formData.country || "PL",
      city: formData.city || undefined,
      street: formData.street || undefined,
      zip_code: formData.zip_code || undefined,
      phone_number: formData.phone_number || undefined,
      email: formData.email || undefined,
      email_invoices: formData.email_invoices || undefined,
      notes: formData.notes || undefined,
    };
    if (formData.opening_hours.length > 0) {
      payload.opening_hours = formData.opening_hours.map((oh) => ({
        ...(oh.id != null && oh.id !== "" ? { id: oh.id } : {}),
        week_day: oh.week_day,
        from_hour: oh.from_hour,
        to_hour: oh.to_hour,
      }));
    }
    return payload;
  };

  const handleSubmit = () => {
    const payload = buildPayload();
    if (id) {
      updatePUT({ id, ...payload }).then((response) => {
        if (response?.success) {
          enqueueSnackbar(t("Form was succesfully processed."), SUCCESS_SNACKBAR);
          onSuccess?.();
        } else {
          enqueueSnackbar(
            (response?.errorData as any)?.message ?? t("There was an error, please review the form."),
            ERROR_SNACKBAR
          );
        }
      });
    } else {
      create(payload).then((response) => {
        if (response?.success) {
          enqueueSnackbar(t("Form was succesfully processed."), SUCCESS_SNACKBAR);
          onSuccess?.();
        } else {
          enqueueSnackbar(
            (response?.errorData as any)?.message ?? t("There was an error, please review the form."),
            ERROR_SNACKBAR
          );
        }
      });
    }
  };

  if (isLoadingInitial) {
    return <CustomCircularProgress />;
  }

  return (
    <Box className={styles.formRoot}>
      <Box className={styles.fieldWrapperFull} sx={{ display: "flex", gap: 0, alignItems: "stretch" }}>
        <Box sx={{ flex: "0 0 50%", minWidth: 0 }}>
          <TextField
            size="small"
            label="NIP"
            placeholder={t("Enter NIP to load company data")}
            value={nipToVerify}
            onPaste={(e) => {
              e.preventDefault();
              const pastedValue = e.clipboardData.getData("text");
              const v = normalizeNip(pastedValue);
              setNipToVerify(v);
              updateField("nip", v);
            }}
            onChange={(e) => {
              const v = normalizeNip(e.target.value);
              setNipToVerify(v);
              updateField("nip", v);
            }}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), verifyNip())}
            fullWidth
            sx={{
              "& .MuiInputBase-root": { color: "var(--textColor)" },
              "& .MuiOutlinedInput-root": { borderTopRightRadius: 0, borderBottomRightRadius: 0 },
              "& .MuiOutlinedInput-notchedOutline": { borderRightWidth: 0 },
            }}
            inputProps={{ maxLength: 13 }}
          />
        </Box>
        <Box sx={{ flex: "0 0 50%", minWidth: 0, display: "flex" }}>
          <Button
            variant="contained"
            color="primary"
            size="medium"
            onClick={verifyNip}
            disabled={verifyLoading}
            fullWidth
            sx={{ height: "100%", minHeight: 40, borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
          >
            {verifyLoading ? t("Fetching...") : t("Fetch the data")}
          </Button>
        </Box>
      </Box>
      {/* Client fields */}
      <Box className={styles.fieldWrapper}>
        <TextField
          size="small"
          label={t("Name")}
          value={formData.name}
          onChange={(e) => updateField("name", e.target.value)}
          fullWidth
          required
        />
        {getError("name", error) && <div className={styles.errorMessage}>{getError("name", error)}</div>}
      </Box>
      <Box className={styles.fieldWrapper}>
        <TextField
          size="small"
          label={t("Email")}
          type="email"
          value={formData.email}
          onChange={(e) => updateField("email", e.target.value)}
          fullWidth
          required
        />
        {getError("email", error) && <div className={styles.errorMessage}>{getError("email", error)}</div>}
      </Box>
      <Box className={styles.fieldWrapper}>
        <TextField
          size="small"
          label={t("Short name")}
          value={formData.short_name}
          onChange={(e) => updateField("short_name", e.target.value)}
          fullWidth
        />
      </Box>
      <Box className={styles.fieldWrapper}>
        <TextField
          size="small"
          label="PESEL"
          value={formData.pesel}
          onChange={(e) => updateField("pesel", e.target.value)}
          fullWidth
          inputProps={{ maxLength: 11 }}
        />
        {getError("pesel", error) && <div className={styles.errorMessage}>{getError("pesel", error)}</div>}
      </Box>
      <Box className={styles.fieldWrapper}>
        <TextField
          size="small"
          label="KRS"
          value={formData.krs}
          onChange={(e) => updateField("krs", e.target.value)}
          fullWidth
        />
        {getError("krs", error) && <div className={styles.errorMessage}>{getError("krs", error)}</div>}
      </Box>
      <Box className={styles.fieldWrapper}>
        <TextField
          size="small"
          label="REGON"
          value={formData.regon}
          onChange={(e) => updateField("regon", e.target.value)}
          fullWidth
        />
        {getError("regon", error) && <div className={styles.errorMessage}>{getError("regon", error)}</div>}
      </Box>
      <Box className={styles.fieldWrapper}>
        <TextField
          size="small"
          label={t("Phone number")}
          value={formData.phone_number}
          onChange={(e) => updateField("phone_number", e.target.value)}
          fullWidth
        />
      </Box>
      <Box className={styles.fieldWrapper}>
        <TextField
          size="small"
          label={t("Email for invoices")}
          type="email"
          value={formData.email_invoices}
          onChange={(e) => updateField("email_invoices", e.target.value)}
          fullWidth
        />
      </Box>
      <Box className={styles.fieldWrapperFull}>
        <TextField
          size="small"
          label={t("Notes")}
          value={formData.notes}
          onChange={(e) => updateField("notes", e.target.value)}
          fullWidth
          multiline
          minRows={3}
        />
      </Box>

      {/* Address group */}
      <Box
        sx={{
          width: "100%",
          border: "1px solid #ccc",
          borderRadius: 2,
          p: 2,
          mt: 1,
        }}
      >
        <Typography variant="h6" sx={{ mb: 2, color: "var(--textColor)" }}>
          {t("Address")}
        </Typography>
        <AddressForm
          value={{
            country: formData.country,
            city: formData.city,
            street: formData.street,
            zip_code: formData.zip_code,
            phone_number: formData.phone_number,
            email: "",
            notes: "",
            opening_hours: formData.opening_hours,
          }}
          onChange={(addr) => {
            setFormData((prev) => ({
              ...prev,
              country: addr.country,
              city: addr.city,
              street: addr.street,
              zip_code: addr.zip_code,
              phone_number: addr.phone_number,
              opening_hours: addr.opening_hours ?? [],
            }));
          }}
          required={["city", "street", "zip_code"]}
          errors={{
            city: getError("city", error) ?? undefined,
            street: getError("street", error) ?? undefined,
            zip_code: getError("zip_code", error) ?? undefined,
          }}
          compact
        />
      </Box>

      <Box className={styles.submitRow}>
        <Button
          variant="contained"
          color="success"
          disabled={loading}
          onClick={handleSubmit}
        >
          {t("Submit")}
        </Button>
      </Box>
    </Box>
  );
}
